import React, { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import queryString from 'query-string';

import { BasePopupActionPayload, BasePopupProps } from 'contexts/PopupContext';
import { MirrorService } from 'services/MirrorService';

import { getTrackingCookie } from 'helpers/analytics/trackingCookies';
import { ORGANIC_PARTNER_IDS } from 'helpers/constants';
import { Funnels, getFunnelSearchString } from 'helpers/funnels/funnels';
import { getRandomNumber } from 'helpers/number';
import { useStickersAnalytics } from 'hooks/analytics/useStickersAnalytics';
import { useStickersQuery } from 'hooks/queries/useStickersQuery';
import { useToast } from 'hooks/useToast';
import { useUpgradePageTransition } from 'hooks/useUpgradePageTransition';
import { getUser } from 'store/auth/selectors';

import { BaseButton, ButtonTypes } from 'components/base/BaseButton';
import { Spinner, SpinnerTypes } from 'components/base/Spinner';
import { Popup } from 'components/UI/Popup';

import css from './initStickerPopup.module.sass';

type ActionsPayload =
  | BasePopupActionPayload<'submit'>
  | BasePopupActionPayload<'close'>;

export interface InitStickerPopupProps {
  threadId: number;
  onSend: (stickerId: number) => void;
}

type Props = BasePopupProps<
  InitStickerPopupProps,
  (action: ActionsPayload) => void
>;

const STICKERS_TO_PREVIEW = 3;
const { partner_id } = queryString.parse(getTrackingCookie());

const INIT_STICKER_PACK_ID =
  MirrorService.isCleanSite ||
  !partner_id ||
  ORGANIC_PARTNER_IDS.includes(String(partner_id))
    ? 2
    : 9;

export const InitStickerPopup: React.FC<Props> = ({
  data: { threadId, onSend },
  onAction,
}) => {
  const { t } = useTranslation('app');

  const { showErrorToast } = useToast();

  const { handleSubmit } = useForm();
  const { handleSafePushUpgradePage } = useUpgradePageTransition();

  const user = useSelector(getUser);

  const { data: stickersData, isLoading: isStickersLoading } = useStickersQuery(
    { packId: INIT_STICKER_PACK_ID },
    {
      onError: () => {
        showErrorToast({
          title: t('thread.sticker.loadFail'),
        });
      },
    }
  );

  const { trackClickStickerPopup, trackCloseStickerPopup } =
    useStickersAnalytics();

  const stickersToPreview = useMemo(() => {
    if (!stickersData?.length) return [];
    if (stickersData?.length <= 3) return stickersData;

    const startIndex = getRandomNumber(
      0,
      stickersData.length - STICKERS_TO_PREVIEW
    );

    return stickersData.slice(startIndex, startIndex + STICKERS_TO_PREVIEW);
  }, [stickersData]);

  const handleClose = useCallback(() => {
    onAction({ type: 'close' });

    trackCloseStickerPopup({ info: 'sticker_send_back' });
  }, [trackCloseStickerPopup]);

  const handleSendClick = useCallback(() => {
    handleClose();

    trackClickStickerPopup({ info: 'sticker_send_back' });

    if (user?.is_premium) {
      onSend(INIT_STICKER_PACK_ID);

      return;
    }

    if (stickersData) {
      const firstSticker = stickersData[0];
      const funnelSearchString = getFunnelSearchString(
        Funnels.StickerMessageV2,
        {
          userId: threadId,
          stickerId: firstSticker.id,
          stickerPackId: firstSticker.sticker_pack_id,
        }
      );

      handleSafePushUpgradePage(funnelSearchString);
    }
  }, [
    handleClose,
    handleSafePushUpgradePage,
    onSend,
    trackClickStickerPopup,
    stickersData,
    threadId,
    user?.is_premium,
  ]);

  return (
    <Popup zIndex={99} onClose={handleClose} customId="sticker_back_popup">
      <div className={css.root}>
        <h2 className={css.title}>{t('stickers.init.title')} </h2>
        <p className={css.subtitle}>{t('stickers.init.subtitle')}</p>
        {isStickersLoading && (
          <div className={css.spinner}>
            <Spinner type={SpinnerTypes.Dark} />
          </div>
        )}

        {!!stickersToPreview?.length && (
          <div className={css.successImages}>
            {stickersToPreview.map((sticker) => (
              <img
                key={sticker.id}
                className={css.successImage}
                src={sticker.big_url || sticker.preview_url}
              />
            ))}
          </div>
        )}

        <p className={css.subtitle}>{t('stickers.bought.more')}</p>
        <form className={css.form} onSubmit={handleSubmit(handleSendClick)}>
          <BaseButton
            nativeType="submit"
            type={ButtonTypes.Accent}
            className={css.sendBtn}
          >
            {t('stickers.buy.send')}
          </BaseButton>
        </form>
      </div>
    </Popup>
  );
};
